/**
 * Master Equipment Testing & Calculation Logic Matrix
 *
 * Encodes the rules from PRD Section 4. Each entry maps an equipment type
 * to its SWL multiplier, Point-5 sentence prefix, and any special notes.
 *
 * Consumed by:
 *   - Frontend forms (reactive Point 5 preview + test-load calc)
 *   - Worker API (server-side PDF generation, validation)
 */

export type LoadTestType =
  | 'EXACT_SWL'
  | 'SWL_MULTIPLIER'
  | 'LOAD_CHART'
  | 'VISUAL_DIMENSIONAL'
  | 'VISUAL_OPERATIONAL'
  | 'NONE';

export interface EquipmentRule {
  /** Machine-readable key, also used as equipment_short_code category. */
  key: string;
  /** Human-readable label shown in dropdowns. */
  label: string;
  /** How the test load is derived from the equipment SWL. */
  loadTestType: LoadTestType;
  /** Multiplier applied to SWL when loadTestType === 'SWL_MULTIPLIER'. */
  multiplier?: number;
  /**
   * Prefix sentence template for Point 5.
   * Placeholders: {load} {date} {inspector}
   */
  point5Prefix: string;
  /**
   * Suffix / additional note appended for certain equipment
   * (e.g. Dynamic load test note for Crane > 10 Ton).
   */
  point5Suffix?: string;
  /** Unit shown after the calculated load value. */
  loadUnit: string;
}

export const EQUIPMENT_RULES: ReadonlyArray<EquipmentRule> = [
  {
    key: 'STACKER',
    label: 'Stacker',
    loadTestType: 'EXACT_SWL',
    point5Prefix:
      'Thorough visual inspection & load test carried out at',
    loadUnit: 'Ton',
  },
  {
    key: 'HOPT',
    label: 'HOPT',
    loadTestType: 'EXACT_SWL',
    point5Prefix:
      'Thorough visual inspection & load test carried out at',
    loadUnit: 'Ton',
  },
  {
    key: 'BOPT',
    label: 'BOPT',
    loadTestType: 'EXACT_SWL',
    point5Prefix:
      'Thorough visual inspection & load test carried out at',
    loadUnit: 'Ton',
  },
  {
    key: 'HPT',
    label: 'HPT',
    loadTestType: 'EXACT_SWL',
    point5Prefix:
      'Thorough visual inspection & load test carried out at',
    loadUnit: 'Ton',
  },
  {
    key: 'CRANE_10',
    label: 'Crane (Upto 10 Ton)',
    loadTestType: 'SWL_MULTIPLIER',
    multiplier: 1.25,
    point5Prefix:
      'Thorough visual inspection & load test carried out at',
    loadUnit: 'Ton',
  },
  {
    key: 'CRANE_GT10',
    label: 'Crane (> 10 Ton, load test possible)',
    loadTestType: 'SWL_MULTIPLIER',
    multiplier: 1.1,
    point5Prefix:
      'Thorough visual inspection & Dynamic load test carried out at',
    loadUnit: 'Ton',
  },
  {
    key: 'EHOIST',
    label: 'Electric Hoist',
    loadTestType: 'SWL_MULTIPLIER',
    multiplier: 1.25,
    point5Prefix:
      'Thorough visual inspection & load test carried out at',
    loadUnit: 'Ton',
  },
  {
    key: 'CPB',
    label: 'Chain Pulley Block (Upto 10 Ton)',
    loadTestType: 'SWL_MULTIPLIER',
    multiplier: 1.5,
    point5Prefix:
      'Thorough visual inspection & load test carried out at',
    loadUnit: 'Ton',
  },
  {
    key: 'FORKLIFT',
    label: 'Fork Lift',
    loadTestType: 'LOAD_CHART',
    point5Prefix:
      'Thorough visual inspection & load test carried out as per load chart at',
    point5Suffix: ' capacity',
    loadUnit: 'Ton',
  },
  {
    key: 'HYDRA',
    label: 'Hydra',
    loadTestType: 'LOAD_CHART',
    point5Prefix:
      'Thorough visual inspection & load test carried out as per load chart at',
    point5Suffix: ' capacity',
    loadUnit: 'Ton',
  },
  {
    key: 'MAGLIFT',
    label: 'Magnetic Lifter',
    loadTestType: 'EXACT_SWL',
    point5Prefix:
      'Thorough visual inspection & load test carried out at',
    loadUnit: 'Ton',
  },
  {
    key: 'DSHACKLE',
    label: "D' Shackle",
    loadTestType: 'VISUAL_DIMENSIONAL',
    point5Prefix:
      'Thorough visual inspection & Dimensional check carried out on',
    loadUnit: '',
  },
  {
    key: 'BOWSHACKLE',
    label: 'Bow Shackle',
    loadTestType: 'VISUAL_DIMENSIONAL',
    point5Prefix:
      'Thorough visual inspection & Dimensional check carried out on',
    loadUnit: '',
  },
  {
    key: 'HCLAMP',
    label: 'Horizontal Clamp',
    loadTestType: 'VISUAL_DIMENSIONAL',
    point5Prefix:
      'Thorough visual inspection & Dimensional check carried out on',
    loadUnit: '',
  },
  {
    key: 'VCLAMP',
    label: 'Vertical Clamp',
    loadTestType: 'VISUAL_DIMENSIONAL',
    point5Prefix:
      'Thorough visual inspection & Dimensional check carried out on',
    loadUnit: '',
  },
  {
    key: 'JACK',
    label: 'Hydraulic Jack',
    loadTestType: 'VISUAL_OPERATIONAL',
    point5Prefix:
      'Thorough visual inspection & operational check carried out on',
    loadUnit: '',
  },
  {
    key: 'BELT',
    label: 'Lifting Belt',
    loadTestType: 'VISUAL_DIMENSIONAL',
    point5Prefix:
      'Thorough visual inspection & Dimensional check carried out on',
    loadUnit: '',
  },
  {
    key: 'SLING',
    label: 'Lifting Sling',
    loadTestType: 'VISUAL_DIMENSIONAL',
    point5Prefix:
      'Thorough visual inspection & Dimensional check carried out on',
    loadUnit: '',
  },
  {
    key: 'HOOK',
    label: 'Lifting Hook',
    loadTestType: 'VISUAL_DIMENSIONAL',
    point5Prefix:
      'Thorough visual inspection & Dimensional check carried out on',
    loadUnit: '',
  },
  {
    key: 'SCISSOR',
    label: 'Scissor Lift',
    loadTestType: 'EXACT_SWL',
    point5Prefix:
      'Thorough visual inspection & load test carried out at',
    loadUnit: 'Ton',
  },
  {
    key: 'SPREADER',
    label: 'Spreader Beam',
    loadTestType: 'VISUAL_OPERATIONAL',
    point5Prefix:
      'Thorough visual inspection & Operational check carried out on',
    loadUnit: '',
  },
] as const;

export const DEFAULT_TEST_METHODS = [
  'Visual inspection & dynamic load test',
  'Visual inspection & load test',
  'Visual inspection & dimensional check',
  'Visual examination & load test as per load chart',
  'Visual inspection',
  'Visual & operational test',
] as const;

export const DEFAULT_TEST_METHOD = 'Visual inspection & load test';

/** Lookup a rule by its key or label with legacy alias support. */
export function getEquipmentRule(keyOrName: string): EquipmentRule | undefined {
  if (!keyOrName) return undefined;
  const target = keyOrName.trim().toUpperCase();

  // 1. Direct match by key
  const byKey = EQUIPMENT_RULES.find((r) => r.key.toUpperCase() === target);
  if (byKey) return byKey;

  // 2. Direct match by label
  const byLabel = EQUIPMENT_RULES.find((r) => r.label.toUpperCase() === target);
  if (byLabel) return byLabel;

  // 3. Fallback aliases for legacy combined types
  if (target.includes('STACKER') || target.includes('STAKER')) {
    return EQUIPMENT_RULES.find((r) => r.key === 'STACKER');
  }
  if (target.includes('HOPT')) {
    return EQUIPMENT_RULES.find((r) => r.key === 'HOPT');
  }
  if (target.includes('BOPT')) {
    return EQUIPMENT_RULES.find((r) => r.key === 'BOPT');
  }
  if (target === 'CLAMP' || target.includes('CLAMP')) {
    return EQUIPMENT_RULES.find((r) => r.key === 'HCLAMP' || r.key === 'VCLAMP');
  }
  if (target === 'SLING' || target.includes('SLING') || target.includes('BELT')) {
    return EQUIPMENT_RULES.find((r) => r.key === 'SLING' || r.key === 'BELT');
  }
  if (target === 'DSHACKLE' || target.includes('SHACKLE')) {
    return EQUIPMENT_RULES.find((r) => r.key === 'DSHACKLE' || r.key === 'BOWSHACKLE');
  }

  return undefined;
}

/**
 * Calculate the test load value shown in Point 5.
 *
 * - EXACT_SWL        → swl as-is
 * - SWL_MULTIPLIER   → swl * multiplier (rounded to 2 dp)
 * - LOAD_CHART       → swl as-is (reference to load chart)
 * - VISUAL_* / NONE  → null (no load value)
 */
export function calculateTestLoad(
  rule: EquipmentRule,
  swl: number | null | undefined,
): string | null {
  switch (rule.loadTestType) {
    case 'EXACT_SWL':
    case 'LOAD_CHART':
      return swl != null ? `${round2(swl)} ${rule.loadUnit}` : null;
    case 'SWL_MULTIPLIER': {
      if (swl == null) return null;
      const val = round2(swl * (rule.multiplier ?? 1));
      return `${val} ${rule.loadUnit}`;
    }
    default:
      return null;
  }
}

/**
 * Build the full Point 5 sentence.
 *
 * @param rule           The equipment rule (optional if custom test method is provided).
 * @param swl            Equipment SWL (tons). May be null for visual-only equipment.
 * @param examinationDate  dd/mm/yyyy string.
 * @param inspectorName  Full name of the competent person.
 * @param testMethod     Optional explicit test method selected by user (defaults to rule's prefix or DEFAULT_TEST_METHOD).
 */
export function buildPoint5Sentence(
  rule: EquipmentRule | undefined,
  swl: number | null | undefined,
  examinationDate: string,
  inspectorName: string,
  testMethod?: string,
): string {
  const method = (testMethod || '').trim();

  // If a test method is explicitly given (or non-empty), format based on test method
  if (method) {
    let baseMethod = method;
    if (!baseMethod.toLowerCase().startsWith('thorough ')) {
      baseMethod = `Thorough ${baseMethod.charAt(0).toLowerCase() + baseMethod.slice(1)}`;
    }

    const lower = baseMethod.toLowerCase();
    const isLoadChart = lower.includes('load chart');
    const isLoadTest = lower.includes('load test');

    const load = rule ? calculateTestLoad(rule, swl) : (swl != null ? `${round2(swl)} Ton` : null);

    let sentence = baseMethod;
    if (!lower.includes('carried out')) {
      if (isLoadChart) {
        if (load) {
          sentence += ` carried out at ${load} capacity on ${examinationDate}`;
        } else {
          sentence += ` carried out on ${examinationDate}`;
        }
      } else if (isLoadTest) {
        if (load) {
          sentence += ` carried out at ${load} on ${examinationDate}`;
        } else {
          sentence += ` carried out on ${examinationDate}`;
        }
      } else {
        sentence += ` carried out on ${examinationDate}`;
      }
    } else {
      if (isLoadTest && load && !sentence.includes(load)) {
        sentence += ` at ${load}`;
      }
      if (!sentence.includes(examinationDate)) {
        sentence += ` on ${examinationDate}`;
      }
    }

    sentence += ` by ${inspectorName}, Competent Person`;
    if (rule?.point5Suffix && !sentence.includes(rule.point5Suffix)) {
      sentence += rule.point5Suffix;
    }
    return sentence;
  }

  // Fallback to legacy rule-based sentence if no testMethod provided
  if (!rule) {
    return `Thorough visual inspection & load test carried out on ${examinationDate} by ${inspectorName}, Competent Person`;
  }

  let sentence = rule.point5Prefix;

  switch (rule.loadTestType) {
    case 'EXACT_SWL':
    case 'LOAD_CHART':
    case 'SWL_MULTIPLIER': {
      const load = calculateTestLoad(rule, swl);
      if (load) {
        sentence += ` ${load} on ${examinationDate}`;
      } else {
        const cleanPrefix = sentence.endsWith(' at') ? sentence.slice(0, -3) : sentence;
        sentence = `${cleanPrefix} on ${examinationDate}`;
      }
      break;
    }
    default: {
      const cleanPrefix = sentence.endsWith(' on') ? sentence.slice(0, -3) : sentence;
      sentence = `${cleanPrefix} on ${examinationDate}`;
      break;
    }
  }

  sentence += ` by ${inspectorName}, Competent Person`;
  if (rule.point5Suffix) sentence += rule.point5Suffix;
  return sentence;
}

/**
 * Build deduplicated equipment type options combining built-in rules and custom types.
 * Prevents duplicate short codes, labels, or keys from appearing in dropdowns.
 */
export function buildEquipmentTypeOptions(
  customTypes: Array<{ key: string; label: string; short_code?: string }> = []
): Array<{ value: string; label: string; short_code: string }> {
  const seenValues = new Set<string>();
  const seenShortCodes = new Set<string>();
  const seenLabels = new Set<string>();
  const options: Array<{ value: string; label: string; short_code: string }> = [];

  // 1. Built-in types
  for (const r of EQUIPMENT_RULES) {
    const valKey = r.key.toUpperCase();
    const labelUpper = r.label.trim().toUpperCase();
    seenValues.add(valKey);
    seenShortCodes.add(valKey);
    seenLabels.add(labelUpper);
    options.push({
      value: r.key,
      label: `${r.label} [${r.key}]`,
      short_code: r.key,
    });
  }

  // 2. Custom types from database
  for (const c of customTypes) {
    if (!c || !c.key) continue;
    const valKey = c.key.toUpperCase();
    const code = (c.short_code || c.key.replace('CUSTOM_', '')).trim().toUpperCase();
    const labelUpper = (c.label || '').trim().toUpperCase();

    // Skip duplicates by key, short_code, or label
    if (seenValues.has(valKey) || (code && seenShortCodes.has(code)) || (labelUpper && seenLabels.has(labelUpper))) {
      continue;
    }

    seenValues.add(valKey);
    if (code) seenShortCodes.add(code);
    if (labelUpper) seenLabels.add(labelUpper);

    options.push({
      value: c.key,
      label: `${c.label} [${code}]`,
      short_code: code,
    });
  }

  return options;
}

/**
 * Build deduplicated test method options combining built-in list and custom user-added methods.
 */
export function buildTestMethodOptions(
  customMethods: Array<{ id?: string; name: string } | string> = []
): Array<{ value: string; label: string }> {
  const seen = new Set<string>();
  const options: Array<{ value: string; label: string }> = [];

  for (const m of DEFAULT_TEST_METHODS) {
    const key = m.trim().toLowerCase();
    seen.add(key);
    options.push({ value: m, label: m });
  }

  for (const c of customMethods) {
    const rawName = typeof c === 'string' ? c : c?.name;
    if (!rawName) continue;
    const name = rawName.trim();
    const key = name.toLowerCase();
    if (!name || seen.has(key)) continue;
    seen.add(key);
    options.push({ value: name, label: name });
  }

  return options;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
